import React, { useState, useEffect, useContext } from 'react';
import { Outlet, useLocation } from "react-router-dom";
import { formatISO9075 } from 'date-fns';
import Skeleton from '@mui/material/Skeleton';
import { Link } from 'react-router-dom';
import {
  Card, CardActionArea, CardMedia, CardContent, Typography, Box, IconButton, Grid,
} from '@mui/material';
import FavoriteIcon from '@mui/icons-material/Favorite';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { BiCommentDetail } from "react-icons/bi";
import Tooltip from '@mui/material/Tooltip';
import { useDBPosts } from '../contexts/DBPostsContext';
import AuthModal from "../components/AuthModal";
import { UserContext } from '../contexts/UserContext';

export default function Post({postInfo, onToggleFavorite, onLoad, OnContextLoad }) {

  const location = useLocation();
  const blogPage = location.pathname === '/blog' || location.pathname === '/blog/' ;

  const [isPostFavorite, setIsPostFavorite] = useState(false);
  const [likesCount, setLikesCount] = useState(0);
  const [viewsCount, setViewsCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false); // New loading state
  const { userInfo, profileLoading } = useContext(UserContext); // Get userInfo and profileLoading




 // Ensure userInfo is available and has loaded before setting favorite status
useEffect(() => {
  if (!profileLoading && postInfo && Array.isArray(postInfo.favoritedBy) && userInfo?.id) {
    const isFavorited = postInfo.favoritedBy.some(favUser => favUser._id === userInfo.id ||favUser === userInfo.id );
    setIsPostFavorite(isFavorited);
    setLikesCount(postInfo.favoritedBy.length);
    
  }
 
}, [postInfo, userInfo, profileLoading]);

  const handleFavoriteClick = async () => {
    if (isLoading) return; // Prevent multiple clicks during loading
    setIsLoading(true); // Start loading

    // Optimistic UI update: Update the favorite state and likes count before the API call
    const wasFavorite = isPostFavorite; // Save the previous favorite state to revert on error
    const previousLikesCount = likesCount; // Save the previous likes count to revert on error
    const newLikesCount = isPostFavorite ? likesCount - 1 : likesCount + 1;

    setIsPostFavorite(!isPostFavorite); // Toggle favorite optimistically
    setLikesCount(newLikesCount); // Update likes count optimistically

    try {
      // Call the parent toggleFavorite function to update on the server
      const result = await onToggleFavorite(postInfo);

      // Ensure the operation was successful and contains updatedPost
      if (result && result.success && result.updatedPost) {
        const updatedPost = result.updatedPost;

        // Update likes count based on the updatedPost
        setLikesCount(updatedPost.favoritedBy.length);

        // Check if the userInfo contains the post as favorite in updatedPost
        const isFavoriteInUpdatedPost = updatedPost.favoritedBy.some(userId => userId === userInfo?.id);

        // Only update isPostFavorite if the new server response contradicts the optimistic change
        if (isFavoriteInUpdatedPost !== isPostFavorite) {
          setIsPostFavorite(isFavoriteInUpdatedPost);
        }

      } else {
        throw new Error("Failed to toggle favorite: Post update was not successful.");
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
      // Revert the optimistic update if the API call fails
      setIsPostFavorite(wasFavorite);
      setLikesCount(previousLikesCount);
    } finally {
      setIsLoading(false); // End loading
    }
  };
  return (
<Card
  sx={{
    display: 'flex',
    flexDirection: blogPage ? { xs: 'column', sm: 'row', md: 'row', lg: 'row' } : 'column', // Always column when !blogPage
    maxWidth: blogPage ? '100%' : 345,
    margin: 'auto',
    boxShadow: 3,
    borderRadius: 2,
    mt: 3,
    position: 'relative',
    minHeight: !blogPage ? 450 : '',
    alignItems: 'center',
  
  }}
>

  {(onLoad  || OnContextLoad) ? (
      <Skeleton     sx={{ height: 140, width: !blogPage ? '100%' : {md: 140, sm:'100%'}}} animation="wave" variant="rectangular" />
  ) : 
  
 ( <CardMedia
    component="img"
    sx={{ height: 140, width: !blogPage ? '100%' : {md: 140, sm:'100%'}}}

    image={postInfo.cover}
    alt={postInfo.title}
  />)
  }

  <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, }}>
    <CardContent>
    

     { (onLoad  || OnContextLoad) ? ( 
      <>
        <Skeleton animation="wave" height={70}  sx={{marginBottom: '1rem'}}/>
        <Skeleton animation="wave" height={70} width="40%" sx={{marginBottom: '1rem'}} />
      </>
     ) : (

      <Link to={`/blog/post/${postInfo._id}`} className='link'>
        <Typography
          variant="h6"
          component="div"
          gutterBottom
          sx={{
            fontSize: { xs: '1.2rem', sm: '1.5rem' },
            display: '-webkit-box',
            overflow: 'hidden',
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: 2, // Limit to 2 lines
          }}
        >
          {postInfo.title}
        </Typography>
      </Link>
     )}

      
     { (onLoad  || OnContextLoad) ? ( 
      <>
        <Skeleton animation="wave" height={50} sx={{marginBottom: '1rem'}} />
        <Skeleton animation="wave" height={50} width="80%" />
      </>
     ) : (

      <Typography
        variant="body2"
        color="textSecondary"
        sx={{
          fontSize: { xs: '0.9rem', sm: '1rem' },
          display: '-webkit-box',
          overflow: 'hidden',
          WebkitBoxOrient: 'vertical',
          WebkitLineClamp: 2, // Limit to 2 lines
        }}
      >
        {postInfo.summary}
      </Typography>
       )}
      {/* Likes and Views */}

      <Grid container display="flex" alignItems="center"  sx={{ flexWrap: 'wrap', mt: 1, paddingBottom: !blogPage ? "2rem" : {xs: '2%', md:'1%'}}}>
        
     { (onLoad  || OnContextLoad) ? ( 
      <>
       
        <Skeleton animation="wave" height={30} width="100%" />
      </>
     ) : (

        <Grid display="flex" flexDirection="row" alignItems="center"  >
 {/* Favorite Button */}
 <IconButton
          disabled={isLoading || !blogPage}
          onClick={handleFavoriteClick}
          sx={{ mr: 1 }}
          className={isPostFavorite ? 'post-icon-active' : 'post-icon'}
        >
          <FavoriteIcon />
        </IconButton>
        <Typography variant="body2" color="textSecondary" sx={{ mr: 2, fontSize: { xs: '0.9rem', sm: '1rem' } }}>
          {likesCount} {likesCount === 1 ? 'Like' : 'Likes'}
        </Typography>
        </Grid>
        )}

        
     { (onLoad  || OnContextLoad) ? ( 
      <>
      <Skeleton animation="wave" height={30} width="100%" />
      </>
     ) : (

        <Grid  display="flex" flexDirection="row" alignItems="center" >
  {/* Views Button */}
  <IconButton disabled className="post-icon" sx={{ mr: 1 }}>
          <VisibilityIcon />
        </IconButton>
        <Typography variant="body2" color="textSecondary" sx={{ mr: 2, fontSize: { xs: '0.9rem', sm: '1rem' } }}>
          {postInfo.views} {postInfo.views === 1 ? 'View' : 'Views'}
        </Typography>
        </Grid>
       )}

{ (onLoad  || OnContextLoad) ? ( 
      <>
      <Skeleton animation="wave" height={30} width="100%" />
      </>
     ) : (
          <Grid display="flex" flexDirection="row" alignItems="center" >

        <IconButton disabled className="post-icon" sx={{ mr: 1 }}>
          <BiCommentDetail />
        </IconButton>
        <Typography variant="body2" color="textSecondary" sx={{ mr: 2, fontSize: { xs: '0.9rem', sm: '1rem' } }}>
          {postInfo.comments.length} {postInfo.comments.length === 1 ? 'Comment' : 'Comments'}
        </Typography>
          </Grid>
         )}
             { (onLoad  || OnContextLoad) ? ( 
      <>
      <Skeleton animation="wave" height={30} width="100%" />
      </>
     ) : (
      <Grid display="flex" flexDirection="row" alignItems="center" >

<Typography
  variant="subtitle2"
  color="textSecondary"
  
  sx={{ fontSize: { xs: '0.8rem', sm: '1rem' } }}
>
  {formatISO9075(new Date(postInfo.createdAt))}
</Typography>
        </Grid>
         )}
      </Grid >

      {postInfo.visability !== 'public' && (
        <Tooltip title="Sign up to access this content for free!">
          <Box
            sx={{
              position: 'absolute',
              bottom: 0,
              right: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              color: 'white',
              p: 1,
              borderRadius: 1, // Add border radius to the tooltip
            }}
          >
            Private Content
          </Box>
        </Tooltip>
      )}
    </CardContent>
  </Box>
</Card>
  );
}
